<template>
  <div class="dcv2">
    <template v-if="section === 'overview'">
      <section class="wrap hero">
        <div class="hero-main">
          <p class="kicker">
            {{ event.dateLabel }} · {{ event.sessionsLabel }} ·
            {{ event.recordedLabel }}
          </p>
          <h1>{{ event.title }}</h1>
          <p v-if="event.lede" class="lede">{{ event.lede }}</p>
          <div class="cta">
            <router-link class="btn primary" :to="watchTo"
              >Watch the recording →</router-link
            >
            <router-link v-if="clips.length" class="btn" :to="secTo('moments')"
              >Jump to a moment</router-link
            >
          </div>
          <p class="prepared">
            <span
              >Prepared by Decast<template v-if="preparedLabel">
                · {{ preparedLabel }}</template
              ></span
            >
            <!-- the hub says the work is verifiable; the credentials for a given
                 recording belong with that recording, not on the front page -->
            <router-link v-if="provenance" class="verifiable" :to="secTo('authenticity')">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z" />
                <path d="M8.8 11.7l2.2 2.2 4.2-4.6" />
              </svg>
              Every session is verifiable
            </router-link>
          </p>
        </div>
      </section>

      <section v-if="strip.length" class="wrap">
        <div class="strip" aria-label="From the footage">
          <router-link
            v-for="(s, i) in strip"
            :key="i"
            class="stat"
            :to="secTo(s.id)"
          >
            <b>{{ s.n }}</b
            ><span>{{ s.label }}</span>
          </router-link>
        </div>
      </section>
    </template>

    <div class="wrap body">
      <main class="main">
        <section v-if="section === 'overview'" id="overview" class="sec">
          <div class="sec-h">
            <span class="kicker">In brief</span
            ><span v-if="versionLabel" class="aside">{{ versionLabel }}</span>
          </div>
          <p class="summary" :class="{ clamp: summaryLong && !moreSummary }">
            {{ summary }}
          </p>
          <button
            v-if="summaryLong"
            class="more"
            type="button"
            @click="moreSummary = !moreSummary"
          >
            {{ moreSummary ? 'Show less' : 'Read the full summary' }}
          </button>
        </section>

        <section
          v-if="section === 'overview' && sentSegs.length"
          class="sec"
        >
          <div class="sec-h">
            <span class="kicker">Insights</span
            ><span class="aside"
              >{{ allFindings.length }} signals pulled from the
              conversation</span
            >
          </div>
          <div class="sent">
            <p class="sent-read">{{ caption }}</p>
            <div class="sbar" role="img" :aria-label="caption">
              <i
                v-for="s in sentSegs"
                :key="s.k"
                :style="{ width: s.pct + '%', background: s.color }"
              ></i>
            </div>
            <div class="chips">
              <router-link
                v-for="s in sentSegs"
                :key="'o' + s.k"
                class="chip"
                :to="sentTo(s.k)"
              >
                <i :style="{ background: s.color }"></i>{{ s.label
                }}<b>{{ s.n }}</b>
              </router-link>
            </div>
          </div>
        </section>

        <section v-if="section === 'overview' && takeaways.length" class="sec">
          <div class="sec-h">
            <span class="kicker">What mattered</span
            ><span class="aside">{{ takeaways.length }} takeaways</span>
          </div>
          <ol class="takes">
            <li v-for="(t, i) in takeaways" :key="'t' + i">
              <span class="n">{{ two(i + 1) }}</span>
              <p>{{ t }}</p>
            </li>
          </ol>
        </section>

        <section v-if="section === 'overview' && actions.length" class="sec">
          <div class="sec-h">
            <span class="kicker">Next steps</span
            ><span class="aside">{{ actions.length }} recommended</span>
          </div>
          <ul class="actions">
            <li v-for="(a, i) in visibleActions" :key="'a' + i">
              <p>{{ a }}</p>
            </li>
          </ul>
          <button
            v-if="actions.length > 4"
            class="more"
            type="button"
            @click="moreActions = !moreActions"
          >
            {{ moreActions ? 'Show fewer' : `Show all ${actions.length}` }}
          </button>
        </section>

        <section v-if="section === 'insights'" id="insights" class="sec">
          <div class="sec-h">
            <span class="kicker">Insights</span
            ><span class="aside"
              >{{ allFindings.length }} signals pulled from the
              conversation</span
            >
          </div>

          <div v-if="sentSegs.length" class="sent">
            <p class="sent-read">{{ caption }}</p>
            <div class="sbar" role="img" :aria-label="caption">
              <i
                v-for="s in sentSegs"
                :key="s.k"
                :style="{ width: s.pct + '%', background: s.color }"
              ></i>
            </div>
            <div class="chips">
              <button
                v-for="s in sentSegs"
                :key="'s' + s.k"
                class="chip"
                :class="{ on: sFilter === s.k }"
                type="button"
                @click="toggle('sFilter', s.k)"
              >
                <i :style="{ background: s.color }"></i>{{ s.label
                }}<b>{{ s.n }}</b>
              </button>
            </div>
          </div>

          <div class="chips kinds">
            <button
              class="chip"
              :class="{ on: !qFilter }"
              type="button"
              @click="qFilter = null"
            >
              All<b>{{ allFindings.length }}</b>
            </button>
            <button
              v-for="k in kinds"
              :key="k.key"
              class="chip"
              :class="{ on: qFilter === k.key }"
              type="button"
              :disabled="!k.n"
              @click="toggle('qFilter', k.key)"
            >
              {{ k.name }}<b>{{ k.n }}</b>
            </button>
          </div>

          <ul v-if="filtered.length" class="findings">
            <li v-for="f in visibleFindings" :key="f._k">
              <router-link
                class="ts"
                :to="seekTo(f)"
                :title="'Open the recording at ' + f.timestamp"
                >{{ f.timestamp }}</router-link
              >
              <span class="fbody">
                <span class="ftype"
                  ><i
                    class="sdot"
                    :style="{ background: sentColor(f.sentiment) }"
                  ></i
                  >{{ f.type
                  }}<template v-if="multi && f._recTag">
                    · {{ f._recTag }}</template
                  ></span
                >
                <span class="ftext">{{ f.text }}</span>
                <span v-if="hasQuote(f)" class="fq">“{{ f.quote }}”</span>
              </span>
            </li>
          </ul>
          <p v-else class="empty">No signals match that filter.</p>
          <button
            v-if="filtered.length > 8"
            class="more"
            type="button"
            @click="moreFindings = !moreFindings"
          >
            {{ moreFindings ? 'Show fewer' : `Show all ${filtered.length}` }}
          </button>
        </section>

        <section v-if="section === 'moments'" id="moments" class="sec">
          <div class="sec-h">
            <span class="kicker">Moments</span>
            <span class="aside"
              >{{ clips.length }} clips<template v-if="shorts.length">
                · {{ shorts.length }} shorts</template
              ></span
            >
          </div>
          <div v-if="clips.length" class="clips">
            <div v-for="(c, i) in clips" :key="'c' + i" class="clip">
              <video
                v-if="playing === 'c' + i"
                class="poster"
                :src="c.url"
                :poster="c.posterUrl || undefined"
                controls
                autoplay
                playsinline
                @ended="playing = null"
              ></video>
              <button
                v-else
                type="button"
                class="poster poster-btn"
                :aria-label="`Play ${c.title}`"
                @click="play('c' + i)"
              >
                <img
                  v-if="c.posterUrl"
                  :src="c.posterUrl"
                  alt=""
                  loading="lazy"
                />
                <span class="play-ic" aria-hidden="true"
                  ><svg viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" fill="currentColor" /></svg
                ></span>
                <span class="ts">{{ c.timestamp }}</span>
                <span v-if="c.length" class="len">{{ fmtLen(c.length) }}</span>
              </button>
              <span class="clip-body">
                <span class="clip-t">{{ c.title }}</span>
                <span v-if="c.blurb" class="clip-b">{{ c.blurb }}</span>
                <router-link class="clip-go" :to="momentTo(c)"
                  >Open in the session →</router-link
                >
              </span>
            </div>
          </div>
          <p v-else class="empty">No clips were cut from this recording.</p>

          <div v-if="shorts.length" class="shorts">
            <span class="kicker sub">Shorts, ready to post</span>
            <div class="shorts-row">
              <div v-for="(s, i) in shorts" :key="'sh' + i" class="short">
                <video
                  v-if="playing === 'sh' + i"
                  class="sposter"
                  :src="s.url"
                  :poster="s.posterUrl || undefined"
                  controls
                  autoplay
                  playsinline
                  @ended="playing = null"
                ></video>
                <button
                  v-else
                  type="button"
                  class="sposter poster-btn"
                  :aria-label="`Play ${s.title}`"
                  @click="play('sh' + i)"
                >
                  <img
                    v-if="s.posterUrl"
                    :src="s.posterUrl"
                    alt=""
                    loading="lazy"
                  />
                  <span class="play-ic" aria-hidden="true"
                    ><svg viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" fill="currentColor" /></svg
                  ></span>
                  <span v-if="s.length" class="len">{{
                    fmtLen(s.length)
                  }}</span>
                </button>
                <span class="short-t">{{ s.title }}</span>
              </div>
            </div>
          </div>
        </section>

        <section v-if="section === 'files'" id="files" class="sec">
          <div class="sec-h">
            <span class="kicker">Files</span
            ><span class="aside">{{ files.length }} downloads</span>
          </div>
          <ul v-if="files.length" class="files">
            <li v-for="f in files" :key="f.url">
              <span class="ficon">
                <svg
                  v-if="f.icon === 'video'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  aria-hidden="true"
                >
                  <rect x="3" y="5" width="14" height="14" rx="2" />
                  <path d="M17 9l4-2v10l-4-2" />
                </svg>
                <svg
                  v-else-if="f.icon === 'audio'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  aria-hidden="true"
                >
                  <path d="M9 18V5l11-2v13" stroke-linecap="round" />
                  <circle cx="6" cy="18" r="2.6" />
                  <circle cx="20" cy="16" r="2.6" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  aria-hidden="true"
                >
                  <path
                    d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z"
                  />
                  <path d="M14 3v5h5" />
                </svg>
              </span>
              <span class="flabel"
                >{{ f.label }}<small v-if="f.group">{{ f.group }}</small></span
              >
              <span class="fsize">{{ f.size }}</span>
              <a class="fdl" :href="f.url" target="_blank" rel="noopener"
                >Download</a
              >
            </li>
          </ul>
          <p v-else class="empty">Nothing to download yet.</p>
          <div v-if="event.storage" class="storage">
            <span
              >Stored on Decast<template v-if="event.storage.label">
                · {{ event.storage.label }}</template
              ><template v-if="event.storage.freeDays">
                · free for {{ event.storage.freeDays }} days</template
              ></span
            >
            <a
              v-if="event.storage.upgradeUrl"
              :href="event.storage.upgradeUrl"
              target="_blank"
              rel="noopener"
              >Keep it longer →</a
            >
          </div>
        </section>

        <section
          v-if="section === 'authenticity'"
          id="authenticity"
          class="sec"
        >
          <div class="sec-h">
            <span class="kicker">Authenticity</span
            ><span v-if="provenance" class="aside">{{
              recordings.length === 1
                ? 'recording signed'
                : `${recordings.length} recordings signed`
            }}</span>
          </div>
          <div v-if="provenance" class="cred">
            <div class="cred-head">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.6 11.7l2.3 2.3 4.5-4.9"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <b>Content Credentials</b>
              <span class="ok">Signed</span>
            </div>
            <p>
              A signed record of where this recording came from and that it has
              not been altered since capture. Download the file and drop it into
              the public verifier to check it yourself.
            </p>
            <dl class="facts">
              <div>
                <dt>Issued by</dt>
                <dd>{{ provenance.issuer }}</dd>
              </div>
              <div v-if="signedAtLabel">
                <dt>Signed</dt>
                <dd>{{ signedAtLabel }}</dd>
              </div>
              <div v-if="provenance.algorithm">
                <dt>Algorithm</dt>
                <dd>{{ String(provenance.algorithm).toUpperCase() }}</dd>
              </div>
              <div v-if="provenance.manifestLabel" class="span2">
                <dt>Manifest</dt>
                <dd>{{ provenance.manifestLabel }}</dd>
              </div>
            </dl>
            <div class="cred-flags">
              <span v-if="provenance.aiDerived"
                >AI-derived assets declared</span
              >
              <span v-if="provenance.transcriptCoupled"
                >Transcript coupled to the recording</span
              >
              <span v-if="provenance.trustPending">Trust listing pending</span>
            </div>
            <div class="cred-links">
              <a :href="provenance.verifyUrl" target="_blank" rel="noopener"
                >Verify the file →</a
              >
              <a
                v-if="reportUrl"
                :href="reportUrl"
                target="_blank"
                rel="noopener"
                >Signed manifest (JSON)</a
              >
            </div>
          </div>
          <p v-else class="empty">This brief has no signed credential.</p>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import {
  sentimentCaption,
  topClips,
  secOf,
  hasQuote,
  QAPO,
  posterFor,
  normalizeProvenance,
} from './useEvent';
import ContentCredentials from '@/components/credentials/ContentCredentials.vue';
import pageProps from './pages/pageProps';

const SENT = {
  positive: ['Positive', '#7fd9b4'],
  neutral: ['Neutral', '#6c7385'],
  negative: ['Negative', '#f0997a'],
  mixed: ['Mixed', '#9887b8'],
};

const SECTIONS = ['overview', 'insights', 'moments', 'files', 'authenticity'];

function longDate(v) {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (e) {
    return String(v);
  }
}

export default {
  name: 'EventBrief',
  components: { ContentCredentials },
  mixins: [pageProps],
  props: {
    // which tab this page shows; the sidebar picks it through the route
    section: { type: String, default: 'overview' },
  },
  data() {
    return {
      copied: false,
      playing: null, // key of the moment playing in place
      moreSummary: false,
      moreActions: false,
      moreFindings: false,
      qFilter: null,
      // a sentiment clicked on the front page arrives as ?s=, so the section opens
      // showing what was asked for rather than everything
      sFilter: this.$route.query.s || null,
    };
  },
  computed: {
    // newest session first, numbered down to 01
    ordered() {
      return this.recordings.slice().reverse();
    },
    multi() {
      return this.recordings.length > 1;
    },
    leadRec() {
      return this.recordings[this.recordings.length - 1] || {};
    },
    poster() {
      return this.leadRec.posterUrl || posterFor(this.leadRec) || '';
    },
    summary() {
      // the series first: a hub of nine sessions opened with the first session's summary,
      // which described one morning in July as though it were the whole programme
      if (this.dashboard.summary) return this.dashboard.summary;
      const r = this.recordings.find((x) => x.analysis && x.analysis.summary);
      return (r && r.analysis.summary) || '';
    },
    summaryLong() {
      return this.summary.length > 700;
    },
    takeaways() {
      return this.dashboard.keyTakeaways || [];
    },
    actions() {
      const out = [];
      this.recordings.forEach((r) =>
        ((r.analysis && r.analysis.recommendedActions) || []).forEach((a) =>
          out.push(a)
        )
      );
      return out;
    },
    visibleActions() {
      return this.moreActions ? this.actions : this.actions.slice(0, 4);
    },
    provenance() {
      for (let i = 0; i < this.recordings.length; i += 1) {
        const p = normalizeProvenance(this.recordings[i]);
        if (p) return p;
      }
      return null;
    },
    reportUrl() {
      const r = this.recordings.find(
        (x) => x.provenance && x.provenance.report_url
      );
      return r ? r.provenance.report_url : '';
    },
    signedAtLabel() {
      return longDate(this.provenance && this.provenance.signedAt);
    },
    // labels of the files the credential covers, for the inspector
    credFiles() {
      const m = this.leadRec.media || {};
      const out = [];
      if (m.video || m.videoUrl) out.push('Main video');
      if (m.audio || m.audioUrl) out.push('Audio');
      if (m.transcriptVtt || (this.leadRec.transcript || []).length)
        out.push('Transcript');
      if (this.reportUrl) out.push('Signing report');
      return out;
    },
    preparedLabel() {
      return longDate(
        this.leadRec.analysis && this.leadRec.analysis.generatedAt
      );
    },
    versionLabel() {
      const v = (this.leadRec.versions || []).find((x) => x.primary);
      if (!v) return '';
      return [v.id, v.label].filter(Boolean).join(' · ');
    },
    caption() {
      return sentimentCaption(this.dashboard.sentiment);
    },
    sentSegs() {
      const s = this.dashboard.sentiment || {};
      const total = Object.values(s).reduce((a, b) => a + b, 0) || 1;
      return ['positive', 'neutral', 'negative', 'mixed']
        .filter((k) => s[k])
        .map((k) => ({
          k,
          label: SENT[k][0],
          color: SENT[k][1],
          n: s[k],
          pct: (s[k] / total) * 100,
        }));
    },
    allFindings() {
      const out = [];
      this.recordings.forEach((r) => {
        ((r.analysis && r.analysis.findings) || []).forEach((f, i) => {
          out.push(
            Object.assign({}, f, {
              _recId: r.id,
              _recTag: r.tag || '',
              _k: `${r.id}-${i}`,
            })
          );
        });
      });
      return out;
    },
    kinds() {
      return QAPO.map((q) => ({
        key: q.key,
        name: q.name,
        n: this.allFindings.filter((f) => (f.qapo || []).indexOf(q.key) !== -1)
          .length,
      }));
    },
    filtered() {
      return this.allFindings.filter(
        (f) =>
          (!this.qFilter || (f.qapo || []).indexOf(this.qFilter) !== -1) &&
          (!this.sFilter ||
            String(f.sentiment || 'neutral').toLowerCase() === this.sFilter)
      );
    },
    visibleFindings() {
      return this.moreFindings ? this.filtered : this.filtered.slice(0, 8);
    },
    clips() {
      return topClips(this.recordings);
    },
    shorts() {
      const out = [];
      this.recordings.forEach((r) =>
        ((r.elements && r.elements.shorts) || []).forEach((s) => out.push(s))
      );
      return out;
    },
    files() {
      const seen = {};
      const out = [];
      const add = (item) => {
        if (!item.url || seen[item.url]) return;
        seen[item.url] = true;
        out.push(item);
      };
      this.recordings.forEach((r) => {
        const m = r.media || {};
        const from = this.multi ? r.title : '';
        if (m.video)
          add({
            icon: 'video',
            label: 'Full recording',
            size: m.video.size || '',
            url: m.video.url,
            group: from,
          });
        if (m.audio)
          add({
            icon: 'audio',
            label: 'Audio only',
            size: m.audio.size || '',
            url: m.audio.url,
            group: from,
          });
        if (m.transcriptVtt)
          add({
            icon: 'file',
            label: 'Transcript (.vtt)',
            size: '',
            url: m.transcriptVtt,
            group: from,
          });
        ((r.elements && r.elements.files) || []).forEach((f) =>
          add({
            icon: 'file',
            label: f.label,
            size: f.size || '',
            url: f.url,
            group: from,
          })
        );
        (r.versions || [])
          .filter((v) => v.kind === 'original')
          .forEach((v) =>
            (v.downloads || []).forEach((d) =>
              add({
                icon: 'file',
                label: d.label,
                size: d.size || '',
                url: d.url,
                group: [from, d.group || v.label || 'Original']
                  .filter(Boolean)
                  .join(' · '),
              })
            )
          );
      });
      return out;
    },
    quickFiles() {
      const m = this.leadRec.media || {};
      const out = [];
      if (m.video)
        out.push({
          label: 'Recording',
          size: m.video.size || '',
          url: m.video.url,
        });
      if (m.audio)
        out.push({
          label: 'Audio',
          size: m.audio.size || '',
          url: m.audio.url,
        });
      if (m.transcriptVtt)
        out.push({ label: 'Transcript', size: '.vtt', url: m.transcriptVtt });
      return out;
    },
    mediaInfo() {
      const m = this.media;
      if (m && Object.keys(m).length) return m;
      return {
        clips: this.clips.length,
        shorts: this.shorts.length,
        transcripts: this.recordings.filter(
          (r) => r.media && r.media.transcriptVtt
        ).length,
      };
    },
    strip() {
      const m = this.mediaInfo;
      return [
        {
          id: 'moments',
          n: m.clips,
          label: m.clips === 1 ? 'highlight' : 'highlights',
        },
        {
          id: 'moments',
          n: m.shorts,
          label: m.shorts === 1 ? 'short' : 'shorts',
        },
        { id: 'insights', n: this.allFindings.length, label: 'insights' },
        {
          id: 'files',
          n: m.transcripts,
          label: m.transcripts === 1 ? 'full transcript' : 'transcripts',
        },
      ].filter((s) => s.n);
    },
    sections() {
      const n = {
        insights: this.allFindings.length,
        moments: this.clips.length + this.shorts.length,
        files: this.files.length,
      };
      const label = {
        overview: 'Overview',
        insights: 'Insights',
        moments: 'Moments',
        files: 'Files',
        authenticity: 'Authenticity',
      };
      return SECTIONS.map((id) => ({ id, label: label[id], n: n[id] || 0 }));
    },
    watchTo() {
      return {
        name: 'Recording',
        params: { id: this.eventId, recId: this.leadRec.id },
      };
    },
  },
  beforeDestroy() {
    clearTimeout(this._copyT);
  },
  methods: {
    hasQuote,
    // one moment plays at a time, in its own card
    play(key) {
      this.playing = key;
    },
    // route for a sidebar tab
    /** The Insights section, already filtered to one sentiment. */
    sentTo(key) {
      return { ...this.secTo('insights'), query: { s: key } };
    },
    secTo(id) {
      const name =
        id === 'overview'
          ? 'Event Hub'
          : `Event ${id.charAt(0).toUpperCase()}${id.slice(1)}`;
      return { name, params: { id: this.eventId } };
    },
    share() {
      const url = window.location.href;
      const title =
        (this.event.share && this.event.share.title) ||
        this.event.title ||
        'Decast';
      if (
        navigator.share &&
        /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
      ) {
        navigator.share({ title, url }).catch(() => {});
        return;
      }
      const done = () => {
        this.copied = true;
        clearTimeout(this._copyT);
        this._copyT = setTimeout(() => {
          this.copied = false;
        }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(url)
          .then(done, () => window.prompt('Copy this link', url));
      } else {
        window.prompt('Copy this link', url);
      }
    },
    toggle(key, v) {
      this[key] = this[key] === v ? null : v;
      this.moreFindings = false;
    },
    sentColor(s) {
      return (SENT[String(s || 'neutral').toLowerCase()] || SENT.neutral)[1];
    },
    signalsOf(r) {
      return (
        ((r.analysis && r.analysis.findings) || []).length ||
        ((r.analysis && r.analysis.dashboard) || {}).insightsTotal ||
        0
      );
    },
    sessionTo(r) {
      return { name: 'Recording', params: { id: this.eventId, recId: r.id } };
    },
    momentTo(c) {
      return {
        name: 'Recording',
        params: { id: this.eventId, recId: c._recId },
        query: { t: String(secOf(c.timestamp)) },
      };
    },
    seekTo(f) {
      return {
        name: 'Recording',
        params: { id: this.eventId, recId: f._recId },
        query: {
          t: String(
            f.seconds != null ? Math.floor(f.seconds) : secOf(f.timestamp)
          ),
        },
      };
    },
    fmtLen(sec) {
      const s = Math.max(0, Math.round(Number(sec) || 0));
      return `${Math.floor(s / 60)}:${('0' + (s % 60)).slice(-2)}`;
    },
    two(n) {
      return ('0' + n).slice(-2);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Product theme. JetBrains Mono comes from App.vue's global rule, so nothing here
   sets a font-family. Lime is for the primary CTA and locators only. */
.dcv2 {
  --bg: #0a0b0f;
  --plate: #ffffff05;
  --plate-2: #ffffff0a;
  --line: #ffffff14;
  --line-soft: #ffffff0a;
  --lime: #d7df23;
  --lime-a: #d7df231f;
  --lime-line: #d7df2340;
  --lime-on: #d3ca57;
  --ink: #0b0c14;
  --tx: #ffffff;
  --tx-2: #ffffffd1;
  --tx-3: #ffffff8f;
  --tx-4: #ffffff59;
  --r: 10px;
  --maxw: 1240px;

  background: var(--bg);
  color: var(--tx);
  min-height: 100vh;
  font-size: 14px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  ul,
  ol {
    list-style: none;
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  img {
    display: block;
    max-width: 100%;
  }
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
  }
  ::selection {
    background: var(--lime);
    color: var(--ink);
  }
  :focus-visible {
    outline: 2px solid var(--lime);
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* the shell already fixes the page width; the brief fills its column */
  .wrap {
    padding-inline: 24px 0;
  }
  .grow {
    flex: 1 1 auto;
  }
  .kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tx-3);
  }
  .empty {
    font-size: 13px;
    color: var(--tx-4);
    padding: 16px 0;
  }
  .msg {
    padding: 80px 24px;
    text-align: center;
    color: var(--tx-3);
  }

  /* buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 18px;
    border-radius: 8px;
    border: 1px solid #ffffff1f;
    background: var(--plate-2);
    font-size: 13px;
    font-weight: 600;
    color: var(--tx);
    transition: background 0.15s, border-color 0.15s;
    &:hover {
      background: #ffffff12;
      border-color: #ffffff40;
    }
    &.primary {
      background: var(--lime);
      border-color: var(--lime);
      color: var(--ink);
    }
    &.primary:hover {
      background: #e3ea3f;
      border-color: #e3ea3f;
    }
    &.sm {
      padding: 9px 14px;
      font-size: 12.5px;
    }
  }
  .more {
    margin-top: 14px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--lime);
    &:hover {
      text-decoration: underline;
    }
  }

  /* top bar */
  .top {
    border-bottom: 1px solid var(--line-soft);
  }
  .top-in {
    height: 56px;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .brand img {
    height: 22px;
  }
  .brand span {
    font-weight: 700;
  }
  .ghost {
    font-size: 12.5px;
    color: var(--tx-3);
    white-space: nowrap;
    &:hover {
      color: var(--tx);
    }
  }
  .verified {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 600;
    color: var(--lime);
    border: 1px solid var(--lime-line);
    border-radius: 999px;
    padding: 5px 11px;
    svg {
      width: 14px;
      height: 14px;
    }
  }
  .share {
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 7px 12px;
    &:hover {
      border-color: #ffffff40;
    }
  }

  /* hero */
  .hero {
    display: block;
    padding-block: 40px 28px;
  }
  .verifiable {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--lime, #d7df23);
    text-decoration: none;
    white-space: nowrap;

    svg {
      width: 15px;
      height: 15px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    &:hover {
      text-decoration: underline;
    }
  }
  .hero-main h1 {
    color: var(--tx);
    margin-top: 14px;
    max-width: 24ch;
    font-size: clamp(1.75rem, 3.2vw, 2.5rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    text-wrap: balance;
  }
  .lede {
    margin-top: 16px;
    font-size: 14.5px;
    line-height: 1.7;
    color: #ffffffb3;
  }
  .cta {
    margin-top: 24px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .prepared {
    margin-top: 22px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--tx-4);
  }
  .player {
    position: relative;
    display: block;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--r);
    border: 1px solid var(--line);
    background: #000;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .play {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      svg {
        width: 64px;
        height: 64px;
        padding: 20px;
        border-radius: 50%;
        background: #0b0c14cc;
        border: 1px solid #ffffff33;
        color: #fff;
        transition: background 0.15s, color 0.15s;
      }
    }
    &:hover .play svg {
      background: var(--lime);
      border-color: var(--lime);
      color: var(--ink);
    }
    .dur {
      position: absolute;
      right: 12px;
      bottom: 12px;
      padding: 4px 8px;
      border-radius: 6px;
      background: #0b0c14d9;
      font-size: 11.5px;
      font-weight: 600;
    }
  }
  .player-cap {
    margin-top: 10px;
    font-size: 12px;
    color: var(--tx-3);
  }

  /* stat strip */
  .strip {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--plate);
    overflow: hidden;
  }
  .stat {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 18px 22px;
    border-right: 1px solid var(--line);
    transition: background 0.15s;
    &:last-child {
      border-right: 0;
    }
    &:hover {
      background: var(--plate-2);
    }
    b {
      font-size: 26px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      line-height: 1;
    }
    span {
      font-size: 12.5px;
      color: var(--tx-3);
    }
  }

  /* sticky section tabs */
  .subnav {
    position: sticky;
    top: 0;
    z-index: 20;
    margin-top: 28px;
    background: #0a0b0fe6;
    backdrop-filter: blur(8px);
    border-top: 1px solid var(--line-soft);
    border-bottom: 1px solid var(--line-soft);
  }
  .subnav-in {
    height: 52px;
    display: flex;
    align-items: center;
    gap: 4px;
    overflow-x: auto;
  }
  .tab {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
    padding: 7px 13px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 500;
    color: #ffffff8c;
    transition: background 0.15s, color 0.15s;
    b {
      font-weight: 500;
      color: var(--tx-4);
    }
    &:hover {
      color: #ffffffd9;
      background: #ffffff08;
    }
    &.on {
      background: var(--lime-on);
      color: var(--ink);
    }
    &.on b {
      color: #0b0c14a6;
    }
  }
  .tab-cta {
    white-space: nowrap;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--lime);
    padding: 7px 4px;
    &:hover {
      text-decoration: underline;
    }
  }

  /* body */
  .body {
    display: block;
    padding-top: 8px;
  }
  .sec {
    padding: 34px 0 40px;
    border-top: 1px solid var(--line-soft);
    scroll-margin-top: 64px;
  }
  .sec:first-child {
    border-top: 0;
  }
  .sec-h {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
  }
  .aside {
    font-size: 12px;
    color: var(--tx-4);
    text-align: right;
  }

  .summary {
    font-size: 14px;
    line-height: 1.75;
    color: var(--tx-2);
    &.clamp {
      display: -webkit-box;
      -webkit-line-clamp: 7;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .takes {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 40px;
    li {
      display: flex;
      gap: 14px;
      padding: 14px 0;
      border-top: 1px solid var(--line-soft);
    }
    .n {
      flex: none;
      min-width: 22px;
      padding-top: 3px;
      font-size: 12px;
      font-weight: 700;
      color: var(--lime);
    }
    p {
      font-size: 13.5px;
      line-height: 1.65;
      color: var(--tx-2);
    }
  }

  .actions {
    li {
      display: flex;
      gap: 14px;
      padding: 12px 0;
      border-top: 1px solid var(--line-soft);
    }
    li::before {
      content: '';
      flex: none;
      width: 7px;
      height: 7px;
      margin-top: 8px;
      background: var(--lime);
    }
    p {
      font-size: 13.5px;
      line-height: 1.65;
      color: var(--tx-2);
    }
  }

  /* insights */
  .sent {
    padding: 18px 20px;
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--plate);
  }
  .sent-read {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }
  .sbar {
    display: flex;
    height: 8px;
    margin-top: 14px;
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff0f;
    i {
      display: block;
      height: 100%;
    }
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
  }
  .kinds {
    margin-top: 18px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 11px;
    border-radius: 999px;
    border: 1px solid var(--line);
    background: var(--plate);
    font-size: 12px;
    color: #ffffffb3;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
    i {
      width: 7px;
      height: 7px;
      border-radius: 50%;
    }
    b {
      font-weight: 500;
      color: var(--tx-4);
    }
    &:hover:not(:disabled) {
      border-color: #ffffff40;
      color: var(--tx);
    }
    &.on {
      border-color: var(--lime);
      color: var(--lime);
      background: var(--lime-a);
    }
    &.on b {
      color: var(--lime);
    }
    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }
  .findings {
    margin-top: 14px;
    li {
      display: grid;
      grid-template-columns: 76px minmax(0, 1fr);
      gap: 16px;
      padding: 13px 0;
      border-top: 1px solid var(--line-soft);
    }
    .ts {
      padding-top: 2px;
      font-size: 12px;
      font-weight: 600;
      color: var(--lime);
      &:hover {
        text-decoration: underline;
      }
    }
    .ftype {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 11px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--tx-3);
    }
    .sdot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
    .ftext {
      display: block;
      margin-top: 5px;
      font-size: 13.5px;
      line-height: 1.6;
      color: var(--tx-2);
    }
    .fq {
      display: block;
      margin-top: 7px;
      padding-left: 10px;
      border-left: 2px solid #ffffff1f;
      font-size: 12.5px;
      line-height: 1.55;
      color: var(--tx-3);
    }
  }

  /* moments */
  .clips {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }
  .clip {
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--plate);
    transition: border-color 0.15s;
    &:hover {
      border-color: #ffffff40;
    }
    .poster {
      position: relative;
      display: block;
      aspect-ratio: 16 / 9;
      background: #000;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .ts,
    .len {
      position: absolute;
      bottom: 10px;
      padding: 3px 7px;
      border-radius: 6px;
      background: #0b0c14d9;
      font-size: 11px;
      font-weight: 600;
    }
    .ts {
      left: 10px;
      color: var(--lime);
    }
    .len {
      right: 10px;
    }
    video.poster {
      width: 100%;
      object-fit: contain;
    }
    .clip-body {
      padding: 0 14px 14px;
    }
    .clip-go {
      display: inline-block;
      margin-top: 8px;
      font-size: 11.5px;
      font-weight: 600;
      color: var(--lime);
      &:hover {
        text-decoration: underline;
      }
    }
    .clip-t {
      display: block;
      font-size: 13.5px;
      font-weight: 600;
      line-height: 1.4;
    }
    .clip-b {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-top: 5px;
      font-size: 12.5px;
      line-height: 1.55;
      color: var(--tx-3);
    }
  }
  .poster-btn {
    appearance: none;
    width: 100%;
    padding: 0;
    border: 0;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: inherit;
    .play-ic {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      opacity: 0;
      transition: opacity 0.15s;
      svg {
        width: 46px;
        height: 46px;
        padding: 13px;
        border-radius: 50%;
        background: #0b0c14cc;
        border: 1px solid #ffffff33;
        color: #fff;
      }
    }
    &:hover .play-ic,
    &:focus-visible .play-ic {
      opacity: 1;
    }
  }
  video.sposter {
    display: block;
    width: 100%;
    aspect-ratio: 9 / 16;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid var(--line);
    background: #000;
  }
  .shorts {
    margin-top: 28px;
  }
  .kicker.sub {
    display: block;
    margin-bottom: 12px;
  }
  .shorts-row {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 6px;
  }
  .short {
    flex: 0 0 132px;
    .sposter {
      position: relative;
      display: block;
      aspect-ratio: 9 / 16;
      overflow: hidden;
      border-radius: 8px;
      border: 1px solid var(--line);
      background: #000;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .len {
      position: absolute;
      right: 8px;
      bottom: 8px;
      padding: 2px 6px;
      border-radius: 5px;
      background: #0b0c14d9;
      font-size: 10.5px;
      font-weight: 600;
    }
    .short-t {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-top: 8px;
      font-size: 12px;
      line-height: 1.4;
      color: var(--tx-2);
    }
    &:hover .sposter {
      border-color: #ffffff40;
    }
  }

  /* files */
  .files li {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) auto auto;
    gap: 16px;
    align-items: center;
    padding: 12px 0;
    border-top: 1px solid var(--line-soft);
  }
  .ficon svg {
    width: 18px;
    height: 18px;
    color: var(--tx-3);
  }
  .flabel {
    font-size: 13.5px;
    small {
      display: block;
      margin-top: 2px;
      font-size: 10.5px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--tx-4);
    }
  }
  .fsize {
    font-size: 12px;
    color: var(--tx-3);
    font-variant-numeric: tabular-nums;
  }
  .fdl {
    padding: 6px 11px;
    border: 1px solid var(--lime-line);
    border-radius: 7px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--lime);
    &:hover {
      background: var(--lime-a);
    }
  }
  .storage {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 18px;
    padding: 14px 16px;
    border: 1px dashed #ffffff26;
    border-radius: var(--r);
    font-size: 12.5px;
    color: var(--tx-3);
    a {
      font-weight: 600;
      color: var(--lime);
    }
  }

  /* authenticity */
  .cred {
    padding: 20px 22px;
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--plate);
  }
  .cred-head {
    display: flex;
    align-items: center;
    gap: 10px;
    svg {
      width: 20px;
      height: 20px;
      color: var(--lime);
    }
    b {
      font-size: 14px;
    }
    .ok {
      margin-left: auto;
      padding: 3px 9px;
      border: 1px solid var(--lime-line);
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--lime);
    }
  }
  .cred > p {
    margin-top: 12px;
    max-width: 70ch;
    font-size: 13px;
    line-height: 1.65;
    color: #ffffffb3;
  }
  .facts {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px 24px;
    margin-top: 18px;
    .span2 {
      grid-column: span 3;
    }
    dt {
      font-size: 11px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--tx-4);
    }
    dd {
      margin-top: 4px;
      font-size: 13px;
      overflow-wrap: anywhere;
    }
  }
  .cred-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
    span {
      padding: 4px 10px;
      border: 1px solid var(--line);
      border-radius: 999px;
      font-size: 11.5px;
      color: #ffffffb3;
    }
  }
  .cred-links {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 18px;
    a {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--lime);
      &:hover {
        text-decoration: underline;
      }
    }
  }

  /* rail */
  .rail {
    position: sticky;
    top: 68px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-top: 34px;
  }
  .rail-box {
    padding: 16px;
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--plate);
    .kicker {
      display: block;
      margin-bottom: 8px;
    }
  }
  .sess {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-top: 1px solid var(--line-soft);
    &:first-of-type {
      border-top: 0;
    }
    .n {
      flex: none;
      font-size: 12px;
      font-weight: 700;
      color: var(--lime);
    }
    .sess-b {
      min-width: 0;
    }
    .sess-t {
      display: block;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.35;
    }
    .sess-m {
      display: block;
      margin-top: 3px;
      font-size: 11.5px;
      color: var(--tx-3);
    }
    svg {
      flex: none;
      width: 14px;
      height: 14px;
      margin-left: auto;
      color: var(--tx-4);
    }
    &:hover .sess-t {
      color: var(--lime);
    }
  }
  .qdl {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 0;
    border-top: 1px solid var(--line-soft);
    font-size: 12.5px;
    color: var(--tx-2);
    &:first-of-type {
      border-top: 0;
    }
    b {
      font-weight: 500;
      color: var(--tx-4);
      font-variant-numeric: tabular-nums;
    }
    &:hover {
      color: var(--lime);
    }
  }
  .rail-more {
    display: block;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--lime);
    &:hover {
      text-decoration: underline;
    }
  }
  .upsell {
    border-color: #d7df2333;
    background: #d7df230a;
    b {
      font-size: 13.5px;
    }
    p {
      margin: 8px 0 14px;
      font-size: 12.5px;
      line-height: 1.6;
      color: #ffffffb3;
    }
  }

  /* footer */
  .foot {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
    margin-top: 24px;
    padding-block: 28px 48px;
    border-top: 1px solid var(--line-soft);
    font-size: 12px;
    color: var(--tx-4);
    a {
      font-weight: 600;
      color: var(--lime);
      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media (max-width: 1080px) {
    .hero {
      grid-template-columns: minmax(0, 1fr) 360px;
      gap: 32px;
    }
    .body {
      grid-template-columns: minmax(0, 1fr);
      gap: 0;
    }
    .rail {
      position: static;
      padding-top: 0;
    }
    .ghost {
      display: none;
    }
  }
  @media (max-width: 820px) {
    .hero {
      grid-template-columns: minmax(0, 1fr);
      padding-block: 28px 20px;
    }
    .strip {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .stat {
      border-bottom: 1px solid var(--line);
      &:nth-child(2n) {
        border-right: 0;
      }
      &:nth-last-child(-n + 2) {
        border-bottom: 0;
      }
    }
    .takes {
      grid-template-columns: minmax(0, 1fr);
    }
    .facts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      .span2 {
        grid-column: span 2;
      }
    }
  }
  @media (max-width: 640px) {
    .clips {
      grid-template-columns: minmax(0, 1fr);
    }
    .findings li {
      grid-template-columns: minmax(0, 1fr);
      gap: 4px;
    }
    .files li {
      grid-template-columns: 24px minmax(0, 1fr) auto;
      .fsize {
        display: none;
      }
    }
    .facts {
      grid-template-columns: minmax(0, 1fr);
      .span2 {
        grid-column: auto;
      }
    }
  }
}
</style>
